// AiResultViewer — terminal step content when the user took the "Let AI
// design" path. Three states it can render:
//   1. LOADING — Generate just fired, waiting on the API. Skeleton +
//      animated progress bar + cycling status text. Same visual family
//      as StepPreview's exponential progress bar.
//   2. RESULT — API succeeded. Renders image(s) + Download + Back.
//   3. EMPTY — defensive (parent should only render with result OR loading
//      true; this catches edge cases like re-mount with nothing).
//
// Single ad routes (Social Post, Product Shot) get one frame at aspect-
// ratio scale. Carousel gets the 2×2 hook→benefit→proof→cta grid with
// per-frame download + a "Download all as zip" bundle.

import { useCallback, useEffect, useState } from 'react';
import type { WizardAiGenerateResult } from '@/lib/api';

interface AiResultViewerProps {
  result: WizardAiGenerateResult | null;
  /** True while the wizardAiGenerate call is in flight. When true (and
   *  result is null), renders the loading skeleton. The parent flips
   *  this in handleAiGenerateStart / handleAiGenerateResult. */
  loading?: boolean;
  /** Carousel vs single — controls the loading skeleton layout (4-tile
   *  grid vs single big tile). When result is present this is inferred
   *  from result.frames.length; this prop is only used during loading. */
  expectedFrameCount?: number;
  /** Error message from the most recent failed wizardAiGenerate call.
   *  When set (and not loading), the viewer renders an error card
   *  with Retry + Back instead of the result. */
  error?: string | null;
  /** Retry handler — re-fires the wizardAiGenerate call without leaving
   *  the step. Only used when `error` is set. Required when error can
   *  surface (i.e. on the Preview step's auto-fire path). */
  onRetry?: () => void;
  /** Sends the user back to the design step to tweak inputs + re-Generate. */
  onBack: () => void;
  /** When true, suppresses the "Baked-in copy" footer block. The /appv3
   *  prototype hides it because the headline + subhead are already baked
   *  into the rendered image — showing them again below is redundant.
   *  Default false to preserve /appv2 behavior. */
  hideCopyBlock?: boolean;
  /** When true, suppresses the result-state action row (Back / Try again /
   *  Download). The /appv3 Studio layout renders its own action row in
   *  the panel footer (Back · Try again w/ quality · Save · Schedule ·
   *  Download), so the in-card buttons would duplicate. Default false to
   *  preserve /appv2 behavior. */
  hideActions?: boolean;
}

// (Cycling "stage" text removed per user feedback — it leaked model name
// implementation details + was visually noisy compared to the template
// path's loader. The single-line "AI is crafting your post" + percentage
// ring matches the template loader, keeping both paths consistent.)

export default function AiResultViewer({ result, loading, expectedFrameCount = 1, error, onRetry, onBack, hideCopyBlock = false, hideActions = false }: AiResultViewerProps) {
  // CRITICAL: every hook must be called on every render in the same order.
  // The component has three render branches (loading / empty / result), so
  // ALL useState + useCallback live up here BEFORE any conditional return.
  // (Original bug: early-return on `loading` ran before useCallback below,
  // so when loading flipped true→false the hook count changed and React
  // crashed. Lesson re-learned.)
  const [zipStatus, setZipStatus] = useState<'idle' | 'zipping' | 'error'>('idle');

  // `result` can be null here (during loading + during the brief empty
  // transition). Use a safe length for hook deps; the conditional returns
  // below short-circuit any actual usage when result is null.
  const framesLength = result?.frames.length ?? 0;
  const isCarousel = framesLength > 1;

  const downloadOne = useCallback((b64: string, filename: string) => {
    const blob = base64ToPngBlob(b64);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, []);

  const downloadAllAsZip = useCallback(async () => {
    if (!result || result.frames.length < 2) return;
    setZipStatus('zipping');
    try {
      const { zip } = await import('fflate');
      const entries: Record<string, Uint8Array> = {};
      for (let i = 0; i < result.frames.length; i++) {
        const blob = base64ToPngBlob(result.frames[i]);
        const buf = new Uint8Array(await blob.arrayBuffer());
        entries[`frame${i + 1}.png`] = buf;
      }
      const zipped: Uint8Array = await new Promise((resolve, reject) => {
        zip(entries, (err, data) => err ? reject(err) : resolve(data));
      });
      const out = new Blob([zipped.buffer as ArrayBuffer], { type: 'application/zip' });
      const url = URL.createObjectURL(out);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-ad-carousel.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setZipStatus('idle');
    } catch (e) {
      console.error('[ai-result] zip failed', e);
      setZipStatus('error');
    }
  }, [result]);

  // ---- Conditional render branches (all hooks above are stable) ----

  // LOADING — Generate just fired, waiting on the API.
  if (loading) {
    return <AiLoadingSkeleton expectedFrameCount={expectedFrameCount} />;
  }

  // ERROR — most recent attempt failed. Show the message + Retry +
  // Back. Retry re-fires the parent's API call without leaving the step.
  if (error) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 px-5 py-6">
        <div className="flex items-start gap-2 mb-3">
          <i className="ri-error-warning-line text-red-600 text-lg mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-red-800">Couldn't generate the ad</p>
            <p className="text-xs text-red-700 mt-0.5">{error}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer"
            >
              <i className="ri-refresh-line" /> Try again
            </button>
          )}
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark-muted text-sm font-bold transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line" /> Back to design
          </button>
        </div>
      </div>
    );
  }

  // EMPTY — defensive; parent should only render this with result or loading.
  if (!result || result.frames.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-surface-dark/40 bg-surface/40 px-5 py-8 text-center">
        <i className="ri-image-line text-2xl text-dark-muted/60" />
        <p className="text-sm font-semibold text-dark mt-2">No AI result yet</p>
        <p className="text-xs text-dark-muted mt-0.5">
          Go back to the design step and click Generate.
        </p>
        <button
          type="button"
          onClick={onBack}
          className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark-muted text-sm font-bold transition-all cursor-pointer"
        >
          <i className="ri-arrow-left-line" /> Back to design
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {isCarousel ? (
        // 2×2 grid with per-frame badge (1..4) + per-frame download.
        // Order matches beat: hook (top-left) → benefit (top-right) →
        // proof (bottom-left) → cta (bottom-right). The shared_style
        // sentence the LLM wrote locks palette/lighting across the 4.
        <div className="grid grid-cols-2 gap-3">
          {result.frames.map((b64, i) => (
            <div
              key={i}
              className="relative rounded-xl border border-surface-dark/25 bg-white overflow-hidden shadow-sm shadow-primary/5"
            >
              <div className="relative aspect-square bg-surface-dark/20">
                <img
                  src={`data:image/png;base64,${b64}`}
                  alt={`Frame ${i + 1}`}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <span className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded bg-black/60 text-white text-[10px] font-bold leading-none">
                  {i + 1}
                </span>
              </div>
              <div className="px-3 py-2 flex items-center justify-between">
                <span className="text-[11px] font-bold text-dark-muted">
                  Frame {i + 1}
                </span>
                <button
                  type="button"
                  onClick={() => downloadOne(b64, `ai-ad-frame${i + 1}.png`)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark-muted text-[10px] font-bold transition-colors cursor-pointer"
                >
                  <i className="ri-download-2-line" /> PNG
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Single ad — render at natural aspect (square in most cases since
        // gpt-image-1 outputs 1024×1024 here). object-contain so we don't
        // crop on screens narrower than the image.
        <div className="rounded-xl border border-surface-dark/25 bg-white p-3 inline-block max-w-full">
          <img
            src={`data:image/png;base64,${result.frames[0]}`}
            alt="AI-generated ad"
            className="max-w-full max-h-[60vh] object-contain rounded-lg"
          />
        </div>
      )}

      {/* Action row — Back + Try again + Download (single) OR Download
          zip + per-frame buttons live inline (carousel). Try again only
          renders when the parent passed onRetry (Preview-step path on
          Product Shot / Carousel — Social Post handles re-roll inline
          on its design step). Suppressed entirely when hideActions
          (the /appv3 Studio footer owns the action row there). */}
      {!hideActions && (
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark-muted text-sm font-bold transition-all cursor-pointer"
        >
          <i className="ri-arrow-left-line" /> Back to design
        </button>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark-muted text-sm font-bold transition-all cursor-pointer"
            title="Re-roll the AI — costs another credit"
          >
            <i className="ri-refresh-line" /> Try again
          </button>
        )}
        {!isCarousel && (
          <button
            type="button"
            onClick={() => downloadOne(result.frames[0], 'ai-ad.png')}
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer"
          >
            <i className="ri-download-2-line" /> Download PNG
          </button>
        )}
        {isCarousel && (
          <button
            type="button"
            onClick={downloadAllAsZip}
            disabled={zipStatus === 'zipping'}
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {zipStatus === 'zipping' ? (
              <><i className="ri-loader-4-line animate-spin" /> Bundling…</>
            ) : zipStatus === 'error' ? (
              <><i className="ri-error-warning-line" /> Retry download</>
            ) : (
              <><i className="ri-download-2-line" /> Download all 4 (.zip)</>
            )}
          </button>
        )}
      </div>
      )}

      {/* Concept text — quiet line at the bottom showing what the LLM
          wrote (so the user can copy-paste the headline elsewhere if
          useful, plus it's a context anchor for the "Try again" decision).
          Hidden when hideCopyBlock=true (e.g. the /appv3 prototype, where
          the headline is already baked into the rendered image). */}
      {!hideCopyBlock && result.concept?.headline && (
        <div className="rounded-lg bg-surface-dark/15 px-3 py-2">
          <p className="text-[10px] font-bold text-dark-muted uppercase tracking-wide mb-0.5">
            Baked-in copy
          </p>
          <p className="text-sm font-semibold text-dark">{result.concept.headline}</p>
          {result.concept.subhead && (
            <p className="text-xs text-dark-muted mt-0.5">{result.concept.subhead}</p>
          )}
        </div>
      )}
    </div>
  );
}

// base64 PNG (without the data: prefix) → Blob for download.
function base64ToPngBlob(b64: string): Blob {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: 'image/png' });
}

// AiLoadingSkeleton — visual + behavioural twin of the template path's
// loading state in StepPreview. Same animated layers (drifting gradient
// + shimmer + 6 sparkles) + same circular percentage ring at the center,
// so AI Generate and Template paths feel like one feature, not two.
//
// Single ad → one aspect-square box with the ring centered.
// Carousel → 2×2 grid of tiles (each gets shimmer + sparkle, no per-tile
//            ring) + a single bottom card with the percentage ring + label.
//
// Progress is an honest exponential curve capped at 92% so we never
// "fake-complete" before the API actually returns. Math matches StepPreview:
//   p(t) = 92 × (1 − exp(−t / τ))  where τ ≈ expectedDuration / ln(5)
// (tuned so progress hits ~80% at the expected wall-clock).
function AiLoadingSkeleton({ expectedFrameCount }: { expectedFrameCount: number }) {
  const isCarousel = expectedFrameCount > 1;
  const expectedMs = isCarousel ? 70_000 : 18_000;
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    const tickStart = Date.now();
    const id = window.setInterval(() => {
      setElapsedMs(Date.now() - tickStart);
    }, 200);
    return () => window.clearInterval(id);
  }, []);

  const tau = expectedMs / Math.log(5);
  const progress = Math.min(92, 92 * (1 - Math.exp(-elapsedMs / tau)));
  const progressPct = Math.round(progress);

  // Six floating sparkle particles — same set as StepPreview's loader.
  // Pre-positioned so they don't all bob in sync.
  const sparkles = [
    { top: '15%', left: '20%', size: 8, delay: '0s',   dur: '3.2s' },
    { top: '25%', left: '78%', size: 6, delay: '0.6s', dur: '2.8s' },
    { top: '60%', left: '12%', size: 10, delay: '1.1s', dur: '3.6s' },
    { top: '70%', left: '85%', size: 7, delay: '0.3s', dur: '3.0s' },
    { top: '40%', left: '90%', size: 5, delay: '1.4s', dur: '2.5s' },
    { top: '85%', left: '45%', size: 9, delay: '0.9s', dur: '3.4s' },
  ];

  return (
    <div className="space-y-3">
      {isCarousel ? (
        // Carousel: 4 shimmering tiles + a centered ring card below.
        <>
          <div className="grid grid-cols-2 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="relative aspect-square rounded-xl overflow-hidden border border-surface-dark/40"
              >
                {/* Drifting gradient backdrop */}
                <div
                  className="absolute inset-0 opacity-60"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(244,114,182,0.18), rgba(99,102,241,0.18), rgba(56,189,248,0.18), rgba(244,114,182,0.18))',
                    backgroundSize: '300% 300%',
                    animation: 'wai-bgshift 8s ease-in-out infinite',
                  }}
                />
                {/* Shimmer sweep */}
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%)',
                    backgroundSize: '200% 100%',
                    animation: 'wai-shimmer 2.4s linear infinite',
                  }}
                />
                {/* Center sparkle */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <i
                    className="ri-sparkling-2-fill text-primary text-3xl"
                    style={{ animation: 'wai-spin 4s linear infinite, wai-twinkle 1.6s ease-in-out infinite' }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center pt-1">
            <ProgressRing progress={progress} progressPct={progressPct} />
          </div>
        </>
      ) : (
        // Single: one aspect-square box with all the layers + the ring centered.
        <div className="relative aspect-square rounded-2xl overflow-hidden border border-surface-dark/40 w-full max-w-md mx-auto">
          {/* Drifting gradient backdrop */}
          <div
            className="absolute inset-0 opacity-60"
            style={{
              background:
                'linear-gradient(135deg, rgba(244,114,182,0.18), rgba(99,102,241,0.18), rgba(56,189,248,0.18), rgba(244,114,182,0.18))',
              backgroundSize: '300% 300%',
              animation: 'wai-bgshift 8s ease-in-out infinite',
            }}
          />
          {/* Shimmer sweep */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%)',
              backgroundSize: '200% 100%',
              animation: 'wai-shimmer 2.4s linear infinite',
            }}
          />
          {/* Floating sparkle particles */}
          <div className="absolute inset-0 pointer-events-none">
            {sparkles.map((p, i) => (
              <i
                key={i}
                className="ri-sparkling-2-fill text-primary absolute"
                style={{
                  top: p.top,
                  left: p.left,
                  fontSize: `${p.size * 2}px`,
                  animation: `wai-float ${p.dur} ease-in-out ${p.delay} infinite, wai-twinkle ${p.dur} ease-in-out ${p.delay} infinite`,
                  opacity: 0.7,
                }}
              />
            ))}
          </div>
          {/* Center cluster — percentage ring + sparkle + label */}
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
            <ProgressRing progress={progress} progressPct={progressPct} />
          </div>
        </div>
      )}

      {/* Keyframes — prefixed `wai-` (wizard-ai) to avoid colliding with
          StepPreview's `sp-` keyframes if both render in the same document. */}
      <style>{`
        @keyframes wai-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        @keyframes wai-bgshift { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
        @keyframes wai-float   { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-10px); } }
        @keyframes wai-twinkle { 0%,100% { opacity: 0.35; transform: scale(0.9); } 50% { opacity: 1; transform: scale(1.15); } }
        @keyframes wai-spin    { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// ProgressRing — the gradient percentage circle used by both the single
// and carousel loaders. Visual twin of StepPreview's center ring (same
// stops, radius, stroke). Caller passes both the numeric progress (0-100,
// for the dashoffset interpolation) AND the rounded display value so we
// don't re-round in two places.
function ProgressRing({ progress, progressPct }: { progress: number; progressPct: number }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-28 h-28">
        <svg className="w-28 h-28 -rotate-90" viewBox="0 0 112 112">
          <defs>
            <linearGradient id="wai-ring" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#f472b6" />
              <stop offset="50%" stopColor="#6366f1" />
              <stop offset="100%" stopColor="#38bdf8" />
            </linearGradient>
          </defs>
          <circle cx="56" cy="56" r="48" fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="8" />
          <circle
            cx="56"
            cy="56"
            r="48"
            fill="none"
            stroke="url(#wai-ring)"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={2 * Math.PI * 48}
            strokeDashoffset={2 * Math.PI * 48 * (1 - progress / 100)}
            style={{ transition: 'stroke-dashoffset 400ms ease-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <i
            className="ri-sparkling-2-fill text-primary text-3xl"
            style={{ animation: 'wai-spin 4s linear infinite, wai-twinkle 1.6s ease-in-out infinite' }}
          />
        </div>
      </div>
      <div className="text-center">
        <p className="font-display font-bold text-dark text-2xl tabular-nums">{progressPct}%</p>
        <p className="text-dark-muted text-xs mt-0.5">AI is crafting your post</p>
      </div>
    </div>
  );
}
